import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SoulHero from "@/components/soul/SoulHero";
import SoulPathCard from "@/components/soul/SoulPathCard";
import SoulBreadcrumb from "@/components/soul/SoulBreadcrumb";
import N8Emblem from "@/components/N8Emblem";
import { SOUL_PATHS } from "@/pages/soul/soulContent";
import { HERO } from "@/lib/heroImages";

/**
 * SoulLanding — the entry point for the Soul experience.
 *
 * Structure:
 *   1. Breadcrumb (Rituals → Soul)
 *   2. Cinematic hero
 *   3. Choose Your Path — two large editorial pathway cards
 *   4. Bottom editorial reflection
 *
 * Deliberately does not surface the eight practices here. The hero and
 * pathway cards do the emotional work; the user chooses their door.
 */
export default function SoulLanding() {
  const nav = useNavigate();
  return (
    <div className="space-y-12" data-testid="soul-landing">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SoulBreadcrumb
          items={[
            { label: "Rituals", to: "/rituals" },
            { label: "Soul" },
          ]}
        />
        <button
          type="button"
          onClick={() => nav("/rituals")}
          data-testid="soul-landing-back"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--copper))] transition-colors"
        >
          <ArrowLeft size={12} /> Back to Rituals
        </button>
      </div>

      <SoulHero
        emblem=""
        eyebrow="Soul"
        title="Go inward."
        supporting="There is more to you than what is visible."
        body="Soul is a space to slow down, listen inward, and explore the patterns, emotions, experiences, and inner knowing shaping who you are becoming."
        image={HERO.soulLanding}
        testid="soul-landing-hero"
      />

      <section
        className="reveal reveal-1"
        data-testid="soul-choose-your-path"
      >
        <div className="max-w-2xl flex items-start gap-4">
          <N8Emblem
            mode="full"
            size={56}
            ariaLabel="N8ONE emblem"
            testid="soul-landing-emblem"
            className="shrink-0 mt-1 hidden sm:inline-flex"
          />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
              Choose Your Path
            </div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic">
              Where would you like to begin?
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--fg))]/80">
              There is no right place to start. Choose the path that meets you where you are today.
            </p>
          </div>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          {SOUL_PATHS.map((p, i) => (
            <SoulPathCard
              key={p.slug}
              eyebrow={p.eyebrow}
              title={p.title}
              tagline={p.tagline}
              body={p.body}
              contains={p.contains}
              image={p.image}
              to={`/soul/${p.slug}`}
              testid={`soul-path-${p.slug}`}
            />
          ))}
        </div>
      </section>

      {/* Bottom editorial reflection — extends the cinematic tone */}
      <section
        className="relative overflow-hidden rounded-[28px] px-6 py-16 md:px-14 md:py-24 text-center"
        data-testid="soul-landing-reflection"
      >
        <div
          aria-hidden
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage: `url("${HERO.soulReflection}")`,
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(30,23,18,0.55) 0%, rgba(60,42,32,0.70) 100%)",
          }}
        />
        <div className="relative z-10 max-w-3xl mx-auto">
          <p
            className="font-display italic text-2xl md:text-4xl leading-tight"
            style={{ color: "#F6F1E8" }}
          >
            The deeper you go inward,
            <br />
            the more of yourself you get to meet.
          </p>
          <p
            className="mt-6 text-[11px] uppercase tracking-[0.32em]"
            style={{ color: "#E19271" }}
          >
            And that changes everything
          </p>
        </div>
      </section>
    </div>
  );
}
